import axios from 'axios'
import CacheStorageService from './cacheStorage.service'

class DigitalVaultDocumentService {
    constructor(apiUrl) {
        this.apiUrl = apiUrl
        this.cache = new CacheStorageService()
        this.cacheUrls = [
            '/categories-by-company',
            '/documents-by-subcategory'
        ]
    }

    /**
    * @author Edgar Escobedo
    * @description GET document info
    * @date 03-11-2023
    * @param {String} documentId
    * @param {String} timezone
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async getDocumentData(documentId, timezone, token) {
        let response = await axios.get(`${this.apiUrl}/registration-document-vault?documentId=${documentId}&timezone=${encodeURIComponent(timezone)}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })

        return response.data
    }


    /**
    * @author Edgar Escobedo
    * @description Update document vault with all data
    * @date 08-11-2023
    * @updatedBy Adriana Fernandez 04-12-23
    * @param {Number} id
    * @param {String} description
    * @param {Date} startDate
    * @param {Date} validation
    * @param {Number} amount
    * @param {Boolean} confidential
    * @param {Boolean} mandatory
    * @param {Boolean} penalty
    * @param {Array} tags
    * @param {String} token
    * @param {Number | String} monetaryUnit
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async updateDocumentVault(id, alias, description, startDate, validation, amount, confidential, notify, mandatory, penalty, tags, entities, workflowExecutionId, variables, token, name, categoryId, companyId, pathBreadcrumb, workflowEXP, monetaryUnit, typeDocument, sendFilesToOCR, dataFilesToOCR, sendFilesToOCRValue) {
        const normalizedTags = Array.isArray(tags) ? tags : []
        const normalizedVariables = Array.isArray(variables) ? variables : []
        const normalizedEntities = Array.isArray(entities) ? { values: entities } : (entities && typeof entities === 'object' ? entities : { values: [] })
        const normalizedCategoryId = (categoryId === undefined) ? null : categoryId
        const normalizedCompanyId = (companyId === undefined) ? null : companyId
        const normalizedPath = typeof pathBreadcrumb === 'string' ? pathBreadcrumb : (pathBreadcrumb ? String(pathBreadcrumb) : '')
        const normalizedDataFilesToOCR = Array.isArray(dataFilesToOCR) ? dataFilesToOCR : []

        const mappedVariables = normalizedVariables.map(v => {
            if (!v) return v
            let value = v.valueVariable
            if (v.typeData === 'date') {
                if (value instanceof Date) {
                    value = value.toISOString()
                } else if (typeof value === 'string') {
                    if (value.length === 0) value = ''
                    else {
                        const d = new Date(value)
                        value = isNaN(d.getTime()) ? '' : d.toISOString()
                    }
                } else {
                    value = ''
                }
            }
            return { ...v, valueVariable: value }
        })

        let data = {
            id,
            alias,
            description,
            startDate,
            validation,
            amount,
            confidential,
            notify,
            mandatory,
            penalty,
            tags: normalizedTags,
            entities: normalizedEntities,
            workflowExecutionId,
            variables: mappedVariables,
            categoryId: normalizedCategoryId,
            companyId: normalizedCompanyId,
            pathBreadcrumb: normalizedPath,
            workflowEXP,
            monetaryUnit,
            typeDocument,
            sendFilesToOCRValue
        }
        if (name) {
            data.name = name
        }

        if (sendFilesToOCR) {
            data.sendFilesToOCR = sendFilesToOCR
            data.dataFilesToOCR = normalizedDataFilesToOCR
        }

        let response = await axios.post(`${this.apiUrl}/registration-document-vault`,
            data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })

        const cache = new CacheStorageService()
        cache.deleteUrlFromCache('/categories-by-company')
        cache.deleteUrlFromCache('/documents-by-subcategory')

        return response.data
    }

    /**
   * @author Edgar Escobedo
   * @updatedBy Adriana Fernandez
   * @description Update document status
   * @date 17-11-2023
   * @param {Array} documentIds
   * @param {String} status
   * @param {String} token
   * @returns {Object}
   * @memberof DigitalVaultDocumentService
   */
    async updateDocumentStatus(documentIds, status, token, fileId) {
        if (!Array.isArray(documentIds)) {
            documentIds = [documentIds]
        }
        let response = await axios.post(`${this.apiUrl}/delete-restore-document`, {
            documentIds, status, fileId
        },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })

        this.cacheUrls.forEach(url => {
            this.cache.deleteUrlFromCache(url)
        })

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description Version a document
    * @date 23-11-2023
    * @param {UUID} documentId
    * @param {Data} documento
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async versionDocument(documento, token) {
        let response = await axios.post(
            `${this.apiUrl}/version-document`,
            documento,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data;
    }

    /**
   * @author Edgar Escobedo
   * @description Make a version mainDoc
   * @date 28-11-2023
   * @param {UUID} documentId
   * @param {String} fileId
   * @param {String} token
   * @returns {Object}
   * @memberof DigitalVaultDocumentService
   */
    async makeVersionMainDoc(documentId, fileId, mainDoc, token) {
        let response = await axios.post(
            `${this.apiUrl}/make-version-main-doc`,
            { documentId, fileId, mainDoc },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data
    }

    /**
  * @author Pedro Antonio
  * @description Renew a document by documentId
  * @date 3-12-2023
  * @param {UUID} documentId
  * @param {UUID} companyId
  * @param {File} renewedDocument
  * @param {String} token
  * @returns {Object}
  * @memberof DigitalVaultDocumentService
  */
    async renewDocument(renewedDocumentData, token) {
        let response = await axios.put(
            `${this.apiUrl}/renew-document`,
            renewedDocumentData,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        return response.data
    }


    /**
    * @author Edgar Escobedo
    * @description GET all permissionRole
    * @date 02-02-2024
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async getAllPermissionRole(token) {
        let response = await axios.get(`${this.apiUrl}/permission-role`,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description add new file to an Expediente
    * @date 16-05-2024
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async addFileToExpediente(documentData, token) {
        let response = await axios.post(`${this.apiUrl}/add-file-expediente`,
            documentData,
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }

        )

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description Reject document
    * @date 24-07-2024
    * @param {UIDD} documentId
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async rejectDocument(documentId, token) {
        let response = await axios.post(`${this.apiUrl}/reject-document`,
            { documentId },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }

        )

        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description GET document information and pages
    * @date 26-08-2024
    * @param {UIDD} documentId
    * @param {String} token
    * @returns {Object}
    * @memberof DigitalVaultDocumentService
    */
    async getDocumentPages(documentId, token) {
        let response = await axios.get(`${this.apiUrl}/document-pages?documentId=${documentId}`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })

        return response.data
    }

    /**
     * @author Edgar Escobedo
     * @description Send document to OCR to extract entities
     * @createdDate 03-09-2025
     * @param {String} documentId - Document id to extract entities.
     * @param {[{id:Number, name:String, pages:Number, process:String, unitary:String, credits:String, value:Boolean}]} documentsToOCR - List of documents to send to OCR.
     * @param {String} token - User token to authorize request.
     * @memberof DigitalVaultDocumentService
     * @returns {Promise<{status: Number, message:String: data: {}}>}
     */
    async sendEntitiesToExtract(documentId, documentsToOCR, token) {
        let response = await axios.post(`${this.apiUrl}/extract-entities`,
            { documentId, documentsToOCR }, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })

        return response.data
    }

    /**
     * @author Marissa Vargas
     * @description Download file by documentId
     * @date 29-08-2024
     * @param {string} documentId - ID of the document to download
     * @param {string} token - Authorization token
     * @returns {Object} - Response data containing the file
     * @memberof DigitalVaultDocumentService
     */
    async downloadFile({ documentId, token }) {
        const url = new URL(`${this.apiUrl}/download-document`)
        url.searchParams.append('documentId', documentId)

        const response = await axios.get(url.toString(), {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        return response.data
    }
}

export { DigitalVaultDocumentService as default }